import InputField from 'components/InputField'
import useBookmarks from 'containers/embed/useBookmarks'
import UserContext from 'containers/user/userContext'
import React, { useContext, useEffect, useState } from 'react'
import DropDownField from 'components/DropDownField'

import { v4 as uuiv4 } from 'uuid'

import gzip from 'gzip-js'
import './Bookmarks.scss'
import { Link, useHistory } from 'react-router-dom'
import Modal from 'components/Modal'
import { Bookmark, DEFAULT_BOOKMARK, DEFAULT_NAV_BOOKMARK, isReportBookmark, NavigationBookmark, PopupType } from 'types'
import PopUpMessage from 'components/PopUpMessage'
import { BookmarksContext } from 'containers/bookmarks/BookmarksContext'

const BookmarkBar = ({
  organizationId,
  embeddedReport,
  reportSlug,
  saveSelectedBookmark,
  setSaveSelectedBookmark
}) => {
  const user = useContext(UserContext)
  const { reportBookmarks, activeBookmark, setActiveBookmark } = useContext(BookmarksContext)

  // const [selectedBookmark, setSelectedBookmark] = useState<Bookmark>(DEFAULT_BOOKMARK)
  const [tempBookmark, setTempBookmark] = useState<Bookmark>(DEFAULT_BOOKMARK)
  const [bookmarks, setBookmarks] = useState<Bookmark[]>([])
  const [popup, setPopup] = useState<PopupType>({
    active: false,
    message: '',
    details: '',
    type: 'success',
    timeout: 3000,
  })
  const [openNamingModal, setOpenNamingModal] = useState(false)
  const [urlReportSlug, setUrlReportSlug] = useState<string | null>(reportSlug)
  const [openSettingsModal, setOpenSettingsModal] = useState(false)
  const [openTestBookmarkModal, setOpenTestBookmarkModal] = useState(false)
  const [secretKeyPattern, setSecretKeyPattern] = useState('')
  const [bookmarkTestData, setBookmarkTestData] = useState('')
  const { batchCreateBookmarks, updateBookmark, getBookmarks, getNavigationBookmarks, bookmarkError } =
    useBookmarks(organizationId)
  const history = useHistory()

  useEffect(() => {
    if (user) {
      setBookmarks((prevBookmarks) => {
        const updatedBookmarks = prevBookmarks.filter((bookmark) => {
          // TODO: actually type check
          // @ts-ignore
          if (bookmark?.is_private && bookmark?.created_by_id !== user.id) {
            return false
          }
          return true
        })
        return updatedBookmarks
      })
    }
  }, [user, reportSlug])

  useEffect(() => {
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        setOpenNamingModal(false)
        setOpenSettingsModal(false)
      }
    })

    document.addEventListener('click', (e) => {
      // TODO: actually type check
      // @ts-ignore
      if (e.target.classList.contains('modal')) {
        setOpenNamingModal(false)
        setOpenSettingsModal(false)
      }
    })

    // get bookmarks from local storage
    const existingBookmarks = localStorage.getItem(
      `${organizationId}-${urlReportSlug}-bookmarks`
    )
    if (existingBookmarks) {
      setBookmarks(JSON.parse(existingBookmarks))
    }

    if (!urlReportSlug) {
      return
    }

    try {
      getBookmarks(urlReportSlug || '').then((bookmarks) => {
        if (bookmarks) {
          // TODO: actually type check
          // @ts-ignore
          setBookmarks(bookmarks)
          localStorage.setItem(
            `${organizationId}-${urlReportSlug}-bookmarks`,
            JSON.stringify(bookmarks)
          )
        }
      })
    } catch (error) {
      console.error('Error fetching bookmarks:', error)
    }

    return () => {
      document.removeEventListener('keydown', (e) => {
        if (e.key === 'Escape') {
          setOpenNamingModal(false)
          setOpenSettingsModal(false)
        }
      })

      document.removeEventListener('click', (e) => {
        // TODO: actually type check
        // @ts-ignore
        if (e.target.classList.contains('modal')) {
          setOpenNamingModal(false)
          setOpenSettingsModal(false)
        }
      })
    }
  }, [reportSlug, urlReportSlug, organizationId, window.location.pathname])

  useEffect(() => {
    if (bookmarkError) {
      setPopup({
        active: true,
        message: bookmarkError.message,
        type: 'error',
        details: bookmarkError.type,
        timeout: 10000,
      })
    }
  }, [bookmarkError])

  useEffect(() => {
    // compare reportBookmarks with bookmarks and update bookmarks if they are different
    if (reportBookmarks && reportBookmarks.length > 0) {
      if (bookmarks !== null && bookmarks.length > 0) {
        if (JSON.stringify(bookmarks) !== JSON.stringify(reportBookmarks)) {
          setBookmarks((prev) => [...prev, ...reportBookmarks])
          localStorage.setItem(
            `${organizationId}-${urlReportSlug}-bookmarks`,
            JSON.stringify(reportBookmarks)
          )
        }
      } else {
        setBookmarks(reportBookmarks)
        localStorage.setItem(
          `${organizationId}-${urlReportSlug}-bookmarks`,
          JSON.stringify(reportBookmarks)
        )
      }
    }
  }, [reportBookmarks])

  useEffect(() => {
    const path = window.location.pathname
    if (path.includes('dashboard')) {
      const match = path.match(/dashboard\/([^/]+)/)?.[1]
      if (match) {
        setUrlReportSlug(match)
      }
    }
  }, [window.location.pathname])

  useEffect(() => {
    ;(async () => {
      if (saveSelectedBookmark) {
        try {
          await triggerSaveBookmark()
        } finally {
          setSaveSelectedBookmark(false)
        }
      }
    })()
  }, [saveSelectedBookmark])


  const triggerSaveBookmark = async () => {
    if (!isReportBookmark(activeBookmark)) return

    embeddedReport.bookmarksManager.config = embeddedReport.config
    const capturedBookmark = await embeddedReport.bookmarksManager.capture()

    const newBookmark = activeBookmark
    newBookmark.state = capturedBookmark.state

    let bookmarks: Bookmark[] = []
    const existingBookmarks = localStorage.getItem(
      `${organizationId}-${urlReportSlug}-bookmarks`
    )
    if (existingBookmarks !== null) {
      bookmarks.push(...JSON.parse(existingBookmarks))
    }

    bookmarks = bookmarks.map((bookmark: Bookmark) => {
      if (bookmark.id == newBookmark.id) return newBookmark
      else return bookmark
    })
    localStorage.setItem(
      `${organizationId}-${urlReportSlug}-bookmarks`,
      JSON.stringify(bookmarks)
    )
    setBookmarks(bookmarks)

    updateBookmark(reportSlug, activeBookmark.id!, newBookmark as Bookmark)
    setPopup({
      active: true,
      message: 'Refreshed bookmark',
      type: 'success',
      details: '',
    })
  }

  const handleReset = () => {
    setActiveBookmark(DEFAULT_NAV_BOOKMARK)
    embeddedReport.reload()
    history.push(
      `/organization/${getStoredOrgId()}/dashboard/${urlReportSlug}`
    )
  }

  const applyBookmark = (bookmarkState: string) => {
    const bookmark = bookmarks.find(b => b?.state === bookmarkState)
    if (!bookmark) {
       setPopup({
        active: true,
        message: "Unable to apply bookmark",
        type: 'error',
        details: "",
        timeout: 10000,
      })
      return
    }
    setActiveBookmark(bookmark)
    history.push(`/organization/${getStoredOrgId()}/dashboard/${urlReportSlug}/bookmarks/${bookmark?.slug}`)
  }

  const captureBookmark = async (embeddedReport) => {
    if (embeddedReport === null) {
      alert("Couldn't capture bookmark; report not loaded")
      return
    }

    embeddedReport.bookmarksManager.config = embeddedReport.config
    const capturedBookmark = await embeddedReport.bookmarksManager.capture()
    tempBookmark.state = capturedBookmark.state

    let bookmarks = []
    const existingBookmarks = localStorage.getItem(
      `${organizationId}-${urlReportSlug}-bookmarks`
    )
    // TODO: actually type check
    // @ts-ignore
    bookmarks.push(tempBookmark)
    if (existingBookmarks !== null) {
      // TODO: actually type check
      // @ts-ignore
      bookmarks.push(...JSON.parse(existingBookmarks))
    }

    localStorage.setItem(
      `${organizationId}-${urlReportSlug}-bookmarks`,
      JSON.stringify(bookmarks)
    )

    setTempBookmark(DEFAULT_BOOKMARK)

    // make a request to add the bookmarks captured to the db
    // We store them locally in "cache in order to have faster access"
    // Only when the cache is empty do we fetch for bookmarks from db
    const response = await batchCreateBookmarks(urlReportSlug || '', bookmarks)

    if (response?.success) {
      // TODO: actually type check
      // @ts-ignore
      setBookmarks(response?.bookmarks)
      const bookmark =
        (response.bookmarks.find(
          (bookmark) => bookmark.state == capturedBookmark.state
        ) as Bookmark) || activeBookmark
      setActiveBookmark(bookmark)
      setPopup({
        active: true,
        message: 'Added bookmark',
        type: 'success',
        details: '',
      })
      return
    }
    setPopup({
      active: true,
      message: 'Error adding bookmark',
      type: 'error',
      details: `Could not add bookmark ${tempBookmark.name}`,
    })
  }

  const getStoredOrgId = () => {
    return localStorage.getItem('currentOrganization')
  }

  useEffect(() => {
    const handleSP = (e) => {
      if (secretKeyPattern.length >= 8) {
        setSecretKeyPattern('')
      } else {
        setSecretKeyPattern((prev) => prev + e.key)
      }
    }
    document.addEventListener('keydown', handleSP)

    return () => {
      document.removeEventListener('keydown', handleSP)
    }
  }, [secretKeyPattern])

  useEffect(() => {
    console.log('Tip: ', secretKeyPattern)
    // @ts-ignore
    if (secretKeyPattern === 'testmaya') {
      setOpenTestBookmarkModal(true)
    }
  }, [secretKeyPattern])

  return (
    <>
      <Modal
        className="naming-modal"
        styles={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}
        onHide={() => setOpenTestBookmarkModal((prev) => !prev)}
        show={openTestBookmarkModal}
      >
        <textarea
          style={{
            height: '521px',
            width: '722px',
          }}
          value={bookmarkTestData}
          onChange={(e) => setBookmarkTestData(e?.target?.value)}
        ></textarea>
        <button
          onClick={() => {
            if (bookmarkTestData?.length != 0) {
              const compressedData = gzip.zip(bookmarkTestData, {})
              const slug = `test-bookmark-${uuiv4().slice(0, 5)}`;
              const testBookmark: Bookmark = {
                id: 99999,
                created_by_id: 428,
                is_active: true,
                is_default: false,
                is_private: false,
                name: slug,
                state: arrayBufferToBase64(compressedData),
                slug,
                created_at: '',
              }
              // @ts-ignore
              setBookmarks((prev) => [...prev, testBookmark])
              setActiveBookmark(testBookmark)
            }
          }}
        >
          Test
        </button>
      </Modal>
      <Modal
        className="naming-modal"
        styles={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}
        onHide={() => setOpenNamingModal(false)}
        show={openNamingModal}
      >
        <h2>Bookmark name</h2>
        <InputField
          type="text"
          label="Bookmark name"
          value={tempBookmark.name}
          setValue={(value) =>
            setTempBookmark((prev) => ({ ...prev, name: value }))
          }
          placeholder="Enter bookmark name"
          error={tempBookmark.name?.length === 0 ? 'Required' : ''}
        />
        <button
          className={`save-bookmark ${
            tempBookmark.name?.length === 0 ? 'disabled' : ''
          }`}
          disabled={tempBookmark.name?.length === 0}
          onClick={() => {
            // check if bookmark name is empty or duplicate
            const name = tempBookmark.name
            if (!name) {
              alert('Bookmark name is required')
              return
            }
            // TODO: actually type check
            // @ts-ignore
            if (bookmarks.some((bookmark) => bookmark?.name === name)) {
              alert('Bookmark name already exists')
              return
            }
            setOpenNamingModal(false)
            captureBookmark(embeddedReport)
          }}
        >
          Save
        </button>
      </Modal>
      {/* {openNamingModal && (
        <div className="modal">
          <div className="modal-content">
            <h2>Bookmark name</h2>

            <InputField
              type="text"
              label="Bookmark name"
              value={tempBookmark.name}
              setValue={(value) => setTempBookmark((prev) => ({ ...prev, name: value }))}
              placeholder="Enter bookmark name"
              error={tempBookmark.name?.length === 0 ? 'Required' : ''}
            />
            <button
              className={`save-bookmark ${tempBookmark.name?.length === 0 ? 'disabled' : ''}`}
              disabled={tempBookmark.name?.length === 0}
              onClick={() => {
                // check if bookmark name is empty or duplicate
                const name = tempBookmark.name
                if (!name) {
                  alert('Bookmark name is required')
                  return
                }
                // TODO: actually type check
            // @ts-ignore
                if (bookmarks.some((bookmark) => bookmark?.name === name)) {
                  alert('Bookmark name already exists')
                  return
                }
                setOpenNamingModal(false)
                captureBookmark(embeddedReport)
              }}
            >
              Save
            </button>
          </div>
        </div>
      )} */}

      {/* { openSettingsModal && 
      <BookmarkSettings 
        user={user}
        reportSlug={reportSlug}
        bookmarks={bookmarks}
        setBookmarks={setBookmarks}
        setSelectedBookmark={setSelectedBookmark}
        setOpenSettingsModal={setOpenSettingsModal}
  
        deleteBookmark={deleteBookmark}
        updateBookmark={updateBookmark}
      />
      } */}

      <div className="bookmark-toolbar">
        <button
          className={`reload-report ${
            !activeBookmark?.id ? 'disabled' : 'tooltip bottom'
          }`}
          disabled={!activeBookmark?.id}
          data-tooltip="Reload Report"
          onClick={handleReset}
        >
          <span className="material-icons-outlined capture non-selectable">
            refresh
          </span>
        </button>
        <DropDownField
          staticLabel="Select a bookmark"
          noBold={true}
          onChange={applyBookmark}
          fixedChild={
            <li
              className="fixed"
              onClick={(e) => {
                e.preventDefault()
                history.push(
                  `/organization/${getStoredOrgId()}/settings/bookmarks`
                )
              }}
            >
              <Link
                to={`/organization/${getStoredOrgId()}/settings/bookmarks`}
                className=""
              >
                Manage Bookmarks
              </Link>
            </li>
          }
          options={[
            { label: 'Select a bookmark', value: '' },
            ...(bookmarks || []).map((bookmark) => ({
              // TODO: actually type check
              // @ts-ignore
              label: bookmark?.name,
              // TODO: actually type check
              // @ts-ignore
              value: bookmark?.state,
            })),
          ]}
          value={activeBookmark && isReportBookmark(activeBookmark) ? activeBookmark?.state : ""}
          // TODO: actually type check
          // @ts-ignore
          placeholder="Select a bookmark"
          filterWithSimilarity
        />
        <button
          className="capture-bookmark tooltip bottom"
          data-tooltip="Capture"
          onClick={() => setOpenNamingModal(true)}
        >
          <span className="material-icons-outlined capture non-selectable">
            bookmark_add
          </span>
        </button>
        {/* <div className="">
            <button className="settings">
                <span onClick={() => setOpenSettingsModal(true)} className="material-icons-outlined settings non-selectable">
                  settings
                </span>
            </button>
        </div> */}
      </div>
      {popup.active && (
        <PopUpMessage
          onClose={() => setPopup((prev) => ({ ...prev, active: false }))}
          message={popup.message}
          type={popup.type}
          details={popup.details}
          time={popup.timeout || 3000}
        />
      )}
    </>
  )
}

export default React.memo(BookmarkBar)

function arrayBufferToBase64(buffer) {
  var binary = ''
  var bytes = new Uint8Array(buffer)
  var len = bytes.byteLength
  for (var i = 0; i < len; i++) {
    binary += String.fromCharCode(bytes[i])
  }
  return window.btoa(binary)
}
